/**
 * Block renderer overrides and additions.
 *
 * Core renders the standard types. This project adds the showcase set, and
 * each is registered in `admin.manifest.ts` so the admin offers it with real
 * fields:
 *
 *   showpiece  the opening stage — the object, named
 *   anatomy    a product shot with annotations pinned to it (the Leader Rule)
 *   features   the built things, one stage each, media and prose alternating
 *   specband   a horizontal band of measured values
 *   code       a listing, as readable text rather than a screenshot
 */

import type { BlockRenderer, MediaItem } from '@remidus/web-core'

import { videoFigure } from '../media-behaviour'
import { renderAnatomy } from './anatomy'
import { renderCode } from './code'
import { renderFeatures } from './features'
import { renderShowpiece } from './showpiece'
import { renderSpecband } from './specband'

/** Video, with the behaviour its own duration earns: short clips loop like a
 *  GIF, films get controls and start when the reader reaches them. */
const renderVideo: BlockRenderer = (block, ctx) => {
  const id = block.data?.media_id
  const item = typeof id === 'string' ? ctx.resolveMedia(id) : undefined
  if (!item) return null
  const figure = videoFigure(item as MediaItem)
  const caption = typeof block.data?.caption === 'string' ? block.data.caption : ''
  if (caption) {
    const line = figure.querySelector('figcaption') ?? document.createElement('figcaption')
    line.className = 'rem-media__caption'
    line.textContent = caption
    if (!line.parentElement) figure.append(line)
  }
  return figure
}

export const blocks: Record<string, BlockRenderer> = {
  showpiece: renderShowpiece,
  anatomy: renderAnatomy,
  features: renderFeatures,
  specband: renderSpecband,
  code: renderCode,
  video: renderVideo,
}

export default blocks
